import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Container,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import api from '../api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const MaintenanceAnalytics = () => {
  console.log('🚀 MaintenanceAnalytics component loaded');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching analytics data...');
      const response = await api.get('/api/maintenance/analytics');
      console.log('✅ Analytics data received:', response.data);
      setAnalyticsData(response.data);
    } catch (err) {
      console.error('❌ Analytics fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!analyticsData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="info">No analytics data available</Alert>
      </Container>
    );
  }

  const {
    overview,
    statusDistribution,
    monthlyTrends,
    costAnalysis,
    propertyPerformance,
    contractorPerformance
  } = analyticsData;

  const MetricCard = ({ title, value, subtitle, color = 'primary' }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography color="textSecondary" gutterBottom variant="h6">
          {title}
        </Typography>
        <Typography variant="h4" component="div" color={color}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="textSecondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Maintenance Analytics
      </Typography>

      {/* Overview Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Requests"
            value={overview.totalRequests}
            subtitle="All time"
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Open Requests"
            value={overview.openRequests}
            subtitle="Currently pending"
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Avg Completion Time"
            value={`${overview.avgCompletionTime} days`}
            subtitle="Last 30 days"
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Costs"
            value={`$${overview.totalCosts.toLocaleString()}`}
            subtitle="All time"
            color="info"
          />
        </Grid>
      </Grid>

      {/* Tabs for different analytics views */}
      <Paper sx={{ width: '100%', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="analytics tabs">
          <Tab label="Status Overview" />
          <Tab label="Monthly Trends" />
          <Tab label="Cost Analysis" />
          <Tab label="Property Performance" />
          <Tab label="Contractor Performance" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Request Status Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statusDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Status Breakdown
                </Typography>
                <Box>
                  {statusDistribution.map((status, index) => (
                    <Box key={status.name} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Box display="flex" alignItems="center">
                        <Box
                          width={16}
                          height={16}
                          borderRadius="50%"
                          bgcolor={COLORS[index % COLORS.length]}
                          mr={1}
                        />
                        <Typography variant="body2">{status.name}</Typography>
                      </Box>
                      <Chip label={status.value} size="small" />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Monthly Request Trends
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={monthlyTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="requests" stroke="#8884d8" strokeWidth={2} />
                <Line type="monotone" dataKey="completed" stroke="#82ca9d" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Cost Analysis by Category
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={costAnalysis}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="totalCost" fill="#8884d8" />
                    <Bar dataKey="avgCost" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top Cost Categories
                </Typography>
                {[...costAnalysis]
                  .sort((a, b) => b.totalCost - a.totalCost)
                  .slice(0, 5)
                  .map((category, index) => (
                    <Box key={category.category} mb={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">{category.category}</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          ${category.totalCost.toLocaleString()}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        {category.requests} requests
                      </Typography>
                    </Box>
                  ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 3 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Property Performance
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Property</TableCell>
                    <TableCell align="right">Total Requests</TableCell>
                    <TableCell align="right">Open Requests</TableCell>
                    <TableCell align="right">Avg Response Time</TableCell>
                    <TableCell align="right">Total Costs</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {propertyPerformance.map((property) => (
                    <TableRow key={property.id}>
                      <TableCell>{property.name}</TableCell>
                      <TableCell align="right">{property.totalRequests}</TableCell>
                      <TableCell align="right">{property.openRequests}</TableCell>
                      <TableCell align="right">{property.avgResponseTime} days</TableCell>
                      <TableCell align="right">${property.totalCosts.toLocaleString()}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={property.status}
                          color={property.openRequests > 0 ? 'warning' : 'success'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {activeTab === 4 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Contractor Performance
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Contractor</TableCell>
                    <TableCell align="right">Assigned Requests</TableCell>
                    <TableCell align="right">Completed</TableCell>
                    <TableCell align="right">Avg Completion Time</TableCell>
                    <TableCell align="right">Total Earnings</TableCell>
                    <TableCell align="center">Rating</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contractorPerformance.map((contractor) => (
                    <TableRow key={contractor.id}>
                      <TableCell>{contractor.name}</TableCell>
                      <TableCell align="right">{contractor.assignedRequests}</TableCell>
                      <TableCell align="right">{contractor.completed}</TableCell>
                      <TableCell align="right">{contractor.avgCompletionTime} days</TableCell>
                      <TableCell align="right">${contractor.totalEarnings.toLocaleString()}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${contractor.rating}/5`}
                          color={contractor.rating >= 4 ? 'success' : contractor.rating >= 3 ? 'warning' : 'error'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default MaintenanceAnalytics; 